import { get, isUndefined } from 'lodash';
import { i18n } from 'vue-infra';
import UserModel from '@core/models/UserModel';
import SkillModel from '@core/models/SkillModel';
import AgentGroupModel from '@core/models/AgentGroupModel';
import UserManagedDataModel from '@core/models/UserManagedDataModel';
import StatusReasonModel from '@core/models/StatusReasonModel';
import BaseClient from '@core/common/BaseClient';
import { RESOURCES } from '@core/clients/AcClient';
import {
  RESOURCES_CONFIGURATION_INTERVAL as INTERVAL,
  UNASSIGNED_SKILL_ID,
  MANAGER_WORKSPACE_SITE_SETTINGS_GROUP,
} from '@config/Consts';
import { parseMetricsConfiguration, extractConfigurationFromSiteSetting } from '@core/utils/utils';

function prepareRequest() {
  const params = {};
  const method = BaseClient.HTTP_METHODS.GET;

  return {
    config: {
      params,
    },
    method,
    interval: INTERVAL.AC,
  };
}

function transformFetchSiteSettingsRequest() {
  const params = {
    groups: MANAGER_WORKSPACE_SITE_SETTINGS_GROUP,
  };
  const method = BaseClient.HTTP_METHODS.GET;

  return {
    config: {
      params,
    },
    method,
    interval: INTERVAL.AC,
  };
}

function transformSetSiteSettingRequest(settings) {
  const params = {
  };
  const method = BaseClient.HTTP_METHODS.PATCH;
  const metricsConfiguration = get(settings, 'value.propertyValue.value.configuration');
  parseMetricsConfiguration(metricsConfiguration, false);

  const request = {
    body: [settings.value],
    config: {
      params,
    },
    method,
    interval: INTERVAL.AC,
  };
  if (settings.revision) {
    request.config.revision = settings.revision;
  }
  return request;
}

function transformUsersResponse(response) {
  let data = null;
  const usersRecords = get(response, 'responseData') || get(response, 'acResponse.data');
  if (!isUndefined(usersRecords)) {
    data = [];
    usersRecords.forEach((record) => {
      const userRecord = new UserModel({
        name: record.fullName,
        id: record.id,
        userType: record.userTypeId,
        memberOf: record.memberOf,
        skillIds: record.skillIds,
      });
      data.push(userRecord);
    });
  }

  return data;
}

function transformSkillsResponse(response) {
  let data = null;
  const skillsRecords = get(response, 'responseData') || get(response, 'acResponse.data');
  if (!isUndefined(skillsRecords)) {
    data = [];
    skillsRecords.forEach((record) => {
      const skillRecord = new SkillModel({
        name: record.name,
        id: record.id,
      });
      data.push(skillRecord);
    });
    const unassignedSkillRecord = new SkillModel({
      name: i18n.t('amd:account_config_unassigned_skill'),
      id: UNASSIGNED_SKILL_ID });
    data.push(unassignedSkillRecord);
  }

  return data;
}

function transformAgentGroupsResponse(response) {
  let data = null;
  const agentGroupsRecords = get(response, 'responseData') || get(response, 'acResponse.data');
  if (!isUndefined(agentGroupsRecords)) {
    data = [];
    agentGroupsRecords.forEach((record) => {
      const agentGroupRecord = new AgentGroupModel({
        name: record.name,
        members: record.members || null,
        id: record.id,
        parentGroupId: record.parentGroupId,
      });
      data.push(agentGroupRecord);
    });
  }

  return data;
}

function transformUserManagedDataResponse(response) {
  let data = null;
  const userManagedData = get(response, 'responseData') || get(response, 'acResponse.data');
  if (!isUndefined(userManagedData)) {
    const userManagedDataRecord = new UserManagedDataModel({
      skills: userManagedData.skillList,
      users: userManagedData.userList,
      agentGroups: userManagedData.agentGroups,
    });
    data = [userManagedDataRecord];
  }
  return data;
}

function transformStatusReasonResponse(response) {
  let data = null;
  const statusReasonRecords = get(response, 'responseData') || get(response, 'acResponse.data');
  if (!isUndefined(statusReasonRecords)) {
    data = [];
    statusReasonRecords.forEach((record) => {
      const statusReasonRecord = new StatusReasonModel({
        text: record.text,
        id: record.id,
      });
      data.push(statusReasonRecord);
    });
  }

  return data;
}

function transformFetchSiteSettingsResponse(response) {
  const siteSettingsArray = get(response, 'data');
  const data = siteSettingsArray && siteSettingsArray.length > 0 ? siteSettingsArray : [];
  const metricsConfigurationSiteSetting = extractConfigurationFromSiteSetting(data);
  parseMetricsConfiguration(metricsConfigurationSiteSetting.metricsConfiguration, true);
  return data;
}

function transformSetUserSiteSettingResponse(response) {
  return get(response, 'data[0].propertyValue');
}

const RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP = {
  [RESOURCES.USER_MANAGED_DATA]: transformUserManagedDataResponse,
  [RESOURCES.AGENT_GROUPS]: transformAgentGroupsResponse,
  [RESOURCES.SKILLS]: transformSkillsResponse,
  [RESOURCES.USERS]: transformUsersResponse,
  [RESOURCES.STATUS_REASON]: transformStatusReasonResponse,
};

function transformResponse(resourceName, response) {
  return RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP[resourceName](response);
}

export default {
  prepareRequest,
  transformResponse,
  transformFetchSiteSettingsResponse,
  transformFetchSiteSettingsRequest,
  transformSetSiteSettingRequest,
  transformSetUserSiteSettingResponse,
};
